import { shapes } from '@joint/plus';
import React, { ChangeEvent, ReactElement, useCallback, useEffect, useRef, useState } from 'react';

import Input from '../Input/Input';
import Select from '../Input/Select/Select';
import { useBaseInspector } from './useBaseInspector';

import { Switch } from '@mui/material';
import ReactQuill from 'react-quill';
import * as LLMService from "../../../../../../../../services/llm.service";
import HelperCustomField from '../../HelperCustomField/HelperCustomField';
import { da } from 'date-fns/locale';

interface BaeList {
    id: number;
    name: string;
}

interface Props {
    cell: shapes.app.LLM;
    chatbotFlowId: string;
}

interface InspectorPort {
    id: string;
    label: string;
    eventId: number
}

const cellProps = {
    label: ['attrs', 'label', 'text'],
    description: ['attrs', 'description', 'text'],
    continuationMessage: ['attrs', 'continuationMessage', 'text'],
    displayText: ['attrs', 'displayText', 'text'],
    icon: ['attrs', 'icon', 'xlinkHref'],
    portLabel: ['attrs', 'portLabel', 'text'],
    eventId: ['attrs', 'eventId'],
    llmId: ['attrs', 'llmId', 'id'],
    isTextRich: ['attrs', 'isTextRich', 'isTextRich'],
    flexibility: ['attrs', 'flexibility', 'value'],
};

const LLMInspector = (props: Props): ReactElement => {

    const { cell, chatbotFlowId } = props;

    const [llmOptions, setLLMOptions] = useState([]);

    const [label, setLabel] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [continuationMessage, setContinuationMessage] = useState<string>('');
    const [llmId, setLLMId] = useState(null);
    const [isTextRich, setIsTextRich] = useState<boolean>(false);
    const [displayText, setDisplayText] = useState<string>(''); 
    const [flexibility, setFlexibility] = useState('static');
    const [isDisplayTextOnFocus, setIsDisplayTextOnFocus] = useState<boolean>(false);

    const [ports, setPorts] = useState<InspectorPort[]>(cell.getGroupPorts('out').map(({ id }) => {
            return {
                id,
                label: cell.portProp(id, cellProps.portLabel),
                eventId: cell.portProp(id, cellProps.eventId),
            };
        }));

    const [canAddPort, setCanAddPort] = useState<boolean>(false);

    const assignFormFields = useCallback((): void => {
        setLabel(cell.prop(cellProps.label));
        setDescription(cell.prop(cellProps.description));
        setLLMId(cell.prop(cellProps.llmId));
        setIsTextRich(cell.prop(cellProps.isTextRich));
        setDisplayText(cell.prop(cellProps.displayText));
        setFlexibility(cell.prop(cellProps.flexibility));
        setContinuationMessage(cell.prop(cellProps.continuationMessage));
    }, [cell]);

    const assignFormPorts = useCallback((): void => {
        setCanAddPort(cell.canAddPort('out'));
        setPorts(cell.getGroupPorts('out').map(({ id }) => {
            return {
                id,
                label: cell.portProp(id, cellProps.portLabel),
                eventId: cell.portProp(id, cellProps.eventId),
            };
        }));
    }, [cell]);

    const changeCellProp = useBaseInspector({ cell, assignFormFields });

    const addCellPort = (name?: string, eventId?: number): void => {
        cell.addDefaultPort(name, eventId);
        assignFormPorts();
    };

    const cleanupPorts = () => {
        const portsToRemove = ports.filter(port => port.eventId && port.eventId !== 0);
        if (portsToRemove.length > 0) {
            cell.removePorts(portsToRemove);
            assignFormPorts();
        }
    };

    const fetchLLMs = async () => {
        try {
            const output = await LLMService.getLLM();

            if (output?.success) {
                setLLMOptions(output?.data ?? []);
            } else {
                setLLMOptions([]);
            }
        } catch (err) {
            setLLMOptions([]);
            console.error(err);
        }
    }

    const fetchListEventDynamicByLLM = async () => {
        try {
            const output = await LLMService.getListEventDynamicsByLLM(llmId);

            if (output?.success) {
                return output?.data ?? [];
            } else {
                return [];
            }
        } catch (err) {
            console.error(err);
            return [];
        }
    }

    const loadComponent = async () => {
        await fetchLLMs();
    }

    const loadEventsPort = async () => {  
        if (isNaN(llmId)) {
            cleanupPorts();
            return;
        }
        const events = await fetchListEventDynamicByLLM() as BaeList[];
        if (events.length === 0) {
            cleanupPorts();
            return;
        }
        const newEvents = ports.length > 0 
            ? events.filter(event => !ports.some(port => port.eventId === event.id))
            : events;
        if (newEvents.length > 0) {
            newEvents.forEach(event => {
                if (event?.id && event?.name) {
                    addCellPort(event.name, event.id);
                }
            });
        }
    }

    useEffect(() => {
        loadComponent();
    }, [])

    useEffect(() => {
        if (llmId) {
            loadEventsPort();
        }
    }, [llmId])

    return (
        <>
            <h1>Componente selecionado</h1>

            <label>Label
                <Input type="text"
                       placeholder="Enter label"
                       value={label}
                       disabled={true}
                />
            </label>

            <div style={{ marginBottom: '8px', marginTop: '8px'}}>
                <Switch
                    value={isTextRich}
                    checked={isTextRich}
                    onChange={(e:any) => changeCellProp(cellProps.isTextRich, e?.target?.checked)}     
                />
                <label>É um texto rico?</label>
            </div>

            {
                isTextRich
                ? (
                    <label>Descrição
                        <div style={{ resize: "vertical", overflow: "auto", height: "250px", marginBottom: "24px", border: "1px solid #ccc" }}>
                            <ReactQuill
                                name="description"
                                style={{ height: '100%' }}
                                fullWidth
                                variant="outlined"
                                id="descriprion"
                                value={description}
                                onFocus={(e) => setIsDisplayTextOnFocus(false)}
                                onChange={(value: any) => changeCellProp(cellProps.description, value)}
                            />
                        </div>
                    </label>
                )
                : (
                    <label>Descrição
                        <Input 
                            type="text"
                            placeholder="Descrição"
                            value={description}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => changeCellProp(cellProps.description, e.target.value)}
                            onSetIsDisplayTextOnFocus={setIsDisplayTextOnFocus}
                        />
                    </label>
                )
            }

            <label>Mensagem de continuação da conversa
                <Input 
                    type="text"
                    placeholder="Mensagem"
                    value={continuationMessage}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => changeCellProp(cellProps.continuationMessage, e.target.value)}
                    onSetIsDisplayTextOnFocus={setIsDisplayTextOnFocus}
                />
            </label>

            <HelperCustomField
                isRichText={isDisplayTextOnFocus ? true : isTextRich}
                description={isDisplayTextOnFocus ? displayText : description}
                param1={isDisplayTextOnFocus ? cellProps.displayText : cellProps.description}
                onChangeDescription={changeCellProp}
                chatbotFlowId={chatbotFlowId}
            />

            <label>LLM
                <Select
                    value={llmId}
                    onChange={(e) => changeCellProp(cellProps.llmId, e.target.value)}
                    options={llmOptions}
                />
            </label>
        </>
    );
};

export default LLMInspector;
